import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  Lock,
  Mail,
  Ticket as TicketIcon,
  Users,
  XCircle,
} from 'lucide-react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { formatDurationSeconds, formatIsoDateTime } from '@/lib/sapJobStatus';
import SapJobLogPanel from '@/components/sapJobs/SapJobLogPanel';
import SapJobOwnershipPanel from '@/components/sapJobs/SapJobOwnershipPanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function Field({ label, children, mono = false }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm break-words ${mono ? 'font-mono' : ''}`}>{children || '—'}</dd>
    </div>
  );
}

function NotifyTile({ icon: Icon, title, state, children }) {
  const tone =
    state === 'ok' ? 'text-cp-ok' : state === 'error' ? 'text-cp-crit' : 'text-muted-foreground';
  const StateIcon = state === 'ok' ? CheckCircle2 : state === 'error' ? XCircle : null;
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-lg border p-3">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {title}
          {StateIcon ? <StateIcon className={`h-3.5 w-3.5 ${tone}`} /> : null}
        </div>
        <div className="break-words text-sm font-medium">{children}</div>
      </div>
    </div>
  );
}

function RunSummary({ run }) {
  return (
    <Card data-testid="failure-run-summary">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Run summary</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Field label="System / client">{[run.sap_system, run.sap_client].filter(Boolean).join(' / ')}</Field>
          <Field label="Job count">{run.job_count}</Field>
          <Field label="Planned start">{formatIsoDateTime(run.planned_start)}</Field>
          <Field label="Actual start">{formatIsoDateTime(run.actual_start)}</Field>
          <Field label="Actual end">{formatIsoDateTime(run.actual_end)}</Field>
          <Field label="Duration">{formatDurationSeconds(run.duration_seconds)}</Field>
          <Field label="Program" mono>{run.program}</Field>
          <Field label="Variant" mono>{run.variant}</Field>
          <Field label="Scheduled by">{run.scheduled_by}</Field>
          <Field label="Evidence status">{run.evidence_status}</Field>
        </dl>
      </CardContent>
    </Card>
  );
}

export default function SapJobFailureTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeComment, setCloseComment] = useState('');
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${INCIDENTS_API}/tickets/${id}`);
      setTicket(data);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (failed || !ticket) {
    return <div className="flex h-full items-center justify-center">Ticket not found</div>;
  }

  const state = ticket.workflow_state || {};
  const report = state.report || {};
  const run = report.run || {};
  const failure = report.failure || {};
  const ownership = report.ownership || state.ownership || null;
  const contact = ownership?.contact || {};
  const notes = state.notifications || {};
  const runs = Array.isArray(state.runs) ? state.runs : [];
  const failureText = failure.text || ticket.error_message || '';
  const code = [failure.message_class, failure.message_number].filter(Boolean).join(' ');

  const copyDescription = async () => {
    try {
      await navigator.clipboard.writeText(ticket.description || '');
      toast.success('Failure details copied');
    } catch {
      toast.error('Could not copy to the clipboard');
    }
  };

  const isClosed = ['completed', 'rejected'].includes(String(ticket.status || '').toLowerCase());
  const closure = state.closure || null;

  const closeTicket = async () => {
    setClosing(true);
    try {
      const comment = closeComment.trim();
      await axios.post(`${INCIDENTS_API}/tickets/${ticket.ticket_number}/close-job-failure`, {
        comment,
      });
      toast.success(
        notes.servicenow_number
          ? `Ticket closed. ServiceNow incident ${notes.servicenow_number} is being resolved.`
          : 'Ticket closed',
      );
      setCloseOpen(false);
      setCloseComment('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not close the ticket');
    } finally {
      setClosing(false);
    }
  };

  const emailState = notes.email_sent_at ? 'ok' : notes.email_error ? 'error' : 'idle';
  const snowState = notes.servicenow_number ? 'ok' : notes.servicenow_error ? 'error' : 'idle';
  const sapNotes = Array.isArray(report.sap_notes) ? report.sap_notes : [];
  const activity = Array.isArray(state.notes) ? [...state.notes].reverse() : [];
  const mappedGroup = contact.assignment_group || '';
  const snowGroup = notes.servicenow_assignment_group || '';
  const groupKept = notes.servicenow_assignment_group_set;

  return (
    <div className="h-full overflow-y-auto" data-testid="sap-job-failure-ticket">
      <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')} aria-label="Back to tickets">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
                <Badge variant="destructive" className="text-xs">SAP JOB FAILURE</Badge>
                <Badge variant="outline" className="text-xs capitalize">{String(ticket.status || '').replace('_', ' ')}</Badge>
                <Badge variant="outline" className="text-xs capitalize">{ticket.priority} priority</Badge>
              </div>
              <h1 className="flex items-start gap-2 text-xl font-semibold">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-cp-crit" />
                <span className="break-words">{ticket.title}</span>
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyDescription}>
              <Copy className="mr-2 h-4 w-4" />
              Copy details
            </Button>
            {!isClosed ? (
              <Button size="sm" onClick={() => setCloseOpen(true)} data-testid="close-ticket-button">
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Close ticket
              </Button>
            ) : null}
            {run.run_id ? (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/sap-job-monitoring/runs/${encodeURIComponent(run.run_id)}`}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View run
                </Link>
              </Button>
            ) : null}
          </div>
        </div>

        {isClosed ? (
          <div className="flex items-start gap-3 rounded-lg border p-3 text-sm" data-testid="failure-closed-banner">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="font-medium">This ticket is closed</div>
              <div className="text-muted-foreground">
                A new failure of this job opens a new ticket.
                {closure?.comment ? <span className="mt-1 block text-foreground">“{closure.comment}”</span> : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid gap-3 md:grid-cols-3" data-testid="failure-notifications">
          <NotifyTile icon={TicketIcon} title="ServiceNow incident" state={snowState}>
            {notes.servicenow_number
              ? (
                <>
                  {notes.servicenow_number}
                  {notes.servicenow_closed_at ? (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">closed in ServiceNow</span>
                  ) : null}
                  {notes.servicenow_close_error ? (
                    <span className="mt-0.5 block text-xs font-normal text-cp-crit">
                      Not closed in ServiceNow: {notes.servicenow_close_error}
                    </span>
                  ) : null}
                </>
              )
              : notes.servicenow_error
                ? <span className="text-cp-crit">Not created: {notes.servicenow_error}</span>
                : <span className="text-muted-foreground">Not created yet</span>}
          </NotifyTile>
          <NotifyTile
            icon={Users}
            title="Assignment group"
            state={mappedGroup && notes.servicenow_number ? (groupKept === false ? 'error' : 'ok') : 'idle'}
          >
            {snowGroup || mappedGroup || <span className="text-muted-foreground">None mapped</span>}
            {mappedGroup && notes.servicenow_number && groupKept === false ? (
              <span className="mt-0.5 block text-xs font-normal text-cp-crit">
                ServiceNow did not keep “{mappedGroup}” on the incident
              </span>
            ) : null}
          </NotifyTile>
          <NotifyTile icon={Mail} title="Email" state={emailState}>
            {notes.email_sent_at
              ? `Sent to ${(notes.email_recipients || []).join(', ')}`
              : notes.email_error
                ? <span className="text-cp-crit">Not sent: {notes.email_error}</span>
                : notes.email_skipped
                  ? <span className="text-muted-foreground">{notes.email_skipped}</span>
                  : <span className="text-muted-foreground">Not sent yet</span>}
          </NotifyTile>
        </div>

        {!state.report ? (
          <Card data-testid="failure-legacy-description">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Details</CardTitle>
              <CardDescription>This ticket was created before full failure reports were stored.</CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap break-words font-mono text-xs">{ticket.description}</pre>
            </CardContent>
          </Card>
        ) : null}

        <Card data-testid="failure-message">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Failure message</CardTitle>
            {code ? <CardDescription className="font-mono">{code}</CardDescription> : null}
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap break-words text-sm">{failureText || '—'}</p>
          </CardContent>
        </Card>

        {sapNotes.length ? (
          <Card data-testid="failure-sap-notes">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">SAP Notes</CardTitle>
              <CardDescription>
                Possible causes and fixes found in SAP Notes when this error first occurred.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {sapNotes.map((note) => (
                <div key={note.id} className="rounded-lg border p-4" data-testid="failure-sap-note">
                  <a
                    href={note.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-cp-indigo hover:underline"
                  >
                    SAP Note {note.id}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <div className="mt-0.5 text-sm font-medium break-words">{note.title}</div>
                  {[
                    ['Symptom', note.symptom],
                    ['Solution', note.solution],
                    ['Summary', note.summary],
                  ]
                    .filter(([, value]) => value)
                    .map(([label, value]) => (
                      <div key={label} className="mt-3">
                        <div className="text-xs text-muted-foreground">{label}</div>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{value}</p>
                      </div>
                    ))}
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-2">
          <RunSummary run={run} />
          <SapJobOwnershipPanel ownership={ownership} />
        </div>

        <Card data-testid="failure-job-log">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">SAP job log</CardTitle>
          </CardHeader>
          <CardContent>
            <SapJobLogPanel lines={report.log_lines} emptyMessage="No log lines were returned for this run." />
          </CardContent>
        </Card>

        <Card data-testid="failure-activity">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Activity</CardTitle>
            <CardDescription>
              Every failure of this job is posted here, and on the ServiceNow incident, until the ticket is closed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activity.length ? (
              <ol className="space-y-4">
                {activity.map((note, index) => (
                  <li key={`${note.at}-${note.run_id}-${index}`} className="border-l-2 pl-4" data-testid="failure-note">
                    <div className="text-xs text-muted-foreground">
                      {formatIsoDateTime(note.at)}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm">{note.message}</p>
                    {note.report_text ? (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-cp-indigo">Full report</summary>
                        <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted/30 p-3 font-mono text-xs">
                          {note.report_text}
                        </pre>
                      </details>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted-foreground">No activity has been recorded yet.</p>
            )}
          </CardContent>
        </Card>

        {runs.length > 1 ? (
          <Card data-testid="failure-occurrences">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Occurrences ({runs.length})</CardTitle>
              <CardDescription>Later runs of this job that failed with the same SAP message.</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y text-sm">
                {runs.map((item) => (
                  <li key={item.run_id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2">
                    <span className="font-mono">{item.job_count || item.run_id}</span>
                    <span className="text-muted-foreground">{formatIsoDateTime(item.actual_end)}</span>
                    <span className="min-w-0 break-words">{item.failure_text}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <AlertDialog open={closeOpen} onOpenChange={(open) => !closing && setCloseOpen(open)}>
        <AlertDialogContent data-testid="close-ticket-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Close this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              {notes.servicenow_number
                ? `ServiceNow incident ${notes.servicenow_number} will be resolved too. `
                : ''}
              If this job fails again, a new ticket is opened.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={closeComment}
            onChange={(e) => setCloseComment(e.target.value)}
            rows={3}
            placeholder="Resolution note (optional)"
            aria-label="Resolution note"
            data-testid="close-ticket-comment"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                closeTicket();
              }}
              disabled={closing}
              data-testid="close-ticket-confirm"
            >
              {closing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Close ticket
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
